// @flow
/** @jsx jsx */

import { ClassNames, jsx, Global, css } from '@emotion/core'
import React, { useState } from 'react'
import Navbar from 'react-bootstrap/lib/Navbar'
import NavItem from 'react-bootstrap/lib/NavItem'
import Nav from 'react-bootstrap/lib/Nav'
import Glyphicon from 'react-bootstrap/lib/Glyphicon'
import { Mutation, Query } from 'react-apollo'
import { Link, Redirect, Route } from 'react-router-dom'
import type { CollectionView } from 'wijmo/wijmo'
import { logoutMutation } from '../graphql/mutations/auth'
import getMenuItems from '../graphql/queries/getMenuItems.queries'
import userSettingsQuery from '../graphql/queries/userSettings.query'
import ActionAlerts from '../components/ActionAlerts'
import ActiveOrderItem from '../components/MenuActiveOrderItem'
import { HistoryContext } from '../context/history.context'
import getActiveOrder from '../graphql/queries/getActiveOrder.query'
import Base from "../layout/Base.layout";
import isLoggedInQuery from "../graphql/queries/isLoggedIn.query";
import { DesktopView, MobileView } from '../config/mediaQuery'
import AppBar from './AppBar'

const qtySerializer = (items) => {
  if (Array.isArray(items)) {
    return items.reduce((reducer, item) => {
      reducer += item.Qty

      return reducer
    }, '')
  }

  return items
}

const ActiveOrderItems = ({
  moduleName,
  selectedOrderType,
  hasUnsavedOrderType,
  isLoading,
  items,
  setActiveOrder,
  mobile = false,
}) => {
  return (
    <Query
      fetchPolicy="network-only"
      query={getActiveOrder}
      variables={{
        // this is just to force a cache invalidation on Apollo since
        // there are cases that network-only alone doesn't make it hit
        // the network ¯\_(ツ)_/¯
        n: qtySerializer(items),
      }}
    >
      {({ data, loading }) => {
        if (!data) return null

        const { activeOrder } = data
        const activeOrderSource = activeOrder || {}

        const hasActiveOrder =
          activeOrderSource.id !== null || activeOrderSource.OrderNo !== null

        mobile && setActiveOrder(activeOrderSource.Account && !!activeOrderSource.Account.AcctNo);

        if (!loading && !hasActiveOrder && moduleName === 'order' && !mobile) {
          return <Redirect to="/accounts" />
        }

        /**
         * orderTypeOverride & hasUnsavedOrderType are both used to show the
         * temporary OrderType along with an message of "unsaved" and the
         * updated order items (some items might not longer be valid depending
         * on the new OrderType or even they might have their qties updated)
         * in case the user has changed the Order Type without saving the
         * order.
         */
        let orderTypeOverride = {}
        if (hasActiveOrder && selectedOrderType) {
          const ot = activeOrderSource.OrderTypes.find(
            (o) => o.value === selectedOrderType,
          )

          if (ot) {
            orderTypeOverride = {
              OrderType: ot.text,
            }
          }
        }
        if (mobile) return null;
        return (
          <ActiveOrderItem
            {...activeOrder}
            {...orderTypeOverride}
            active={activeOrder && activeOrder.Account !== null}
            hasUnsavedOrderType={hasUnsavedOrderType}
            isLoading={loading}
          />
        )
      }}
    </Query>
  )
}

const Header = (props: {
  moduleName?: string,
  items?: CollectionView,
  selectedOrderType?: string,
  hasUnsavedOrderType?: boolean,
  isLoading?: boolean,
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuTitle, setMenuTitle] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);
  const { moduleName, selectedOrderType, hasUnsavedOrderType, items } = props

  // We fire this query, to check if user is logged in, when user changes page.
  // We don't need to control result of this query, as we have isLoggedInLink of Apollo,
  // that does this check for us and reloads when necessary.
  const isUserLoggedIn = () => {
    Base.getClient().query({query: isLoggedInQuery, fetchPolicy: 'network-only'})
  }

  const logoutComponent = (mobile = false, mobileView = null) => {
    return (
      <HistoryContext.Consumer>
        {(history) => (
          <Mutation
            mutation={logoutMutation}
            refetchQueries={() => {
              return ['IsLoggedIn']
            }}
            onCompleted={() => {
              // we need to force a page reload after loggin out
              // bc cache.reset() will also remove local state
              window.location.href = '/login'
            }}
            update={(cache) => {
              // $FlowFixMe
              cache.reset()
            }}
          >
            {(logoutUser, options) => {
              // We perform check of login every 5 min,
              // and if he is not logged in anymore - he logs out automatically.
              return (mobile ?
                mobileView(logoutUser)
                :
                <NavItem
                  eventKey={2}
                  href="#"
                  onClick={() => {
                    logoutUser({}).catch(() => {})
                  }}
                >
                  <Glyphicon glyph="log-out" />
                  {options.loading ? 'Logging out...' : 'Logout'}
                </NavItem>
              )
            }}
          </Mutation>
        )}
      </HistoryContext.Consumer>
    )
  }

  return (
    <React.Fragment>
      <Global
        styles={css`
          html, body {
            overflow-x: hidden;
          }
        `}
      />
      <Query
        query={userSettingsQuery}
        variables={{
          moduleName,
        }}
      >
        {({ data: dataGrid, loading }: any) => {
          if (
            dataGrid &&
            dataGrid.userSettings &&
            Array.isArray(dataGrid.userSettings.notifications)
          ) {
            return dataGrid.userSettings.notifications.map((i) => {
              return (
                <ActionAlerts
                  key={i.id}
                  moduleName={i.moduleName}
                  namespace={i.namespace}
                  anouncements={i.anouncements}
                />
              )
            })
          }

          return null
        }}
      </Query>
      <Query query={getMenuItems}>
        {({ data, loading }: any) => {
          if (!data) return null

          const { menuItems } = data

          menuItems &&
            menuItems.every((item) => {
              if (item.text.toLowerCase() === moduleName) {
                document.title = item.text
                setMenuTitle(item.text);
                return false
              }
              return true
            })
          return (
            <>
              <DesktopView>
                <Navbar
                  inverse
                  collapseOnSelect
                  staticTop
                  fluid
                  css={{
                    marginBottom: '20px',
                  }}
                >
                  <Navbar.Header>
                    <Navbar.Brand>
                      <a href="/">Polaner Sales Portal</a>
                    </Navbar.Brand>
                    <Navbar.Toggle />
                  </Navbar.Header>
                  <Navbar.Collapse>
                    <Nav pullRight>
                      <React.Fragment>
                        <ActiveOrderItems
                          moduleName={moduleName}
                          selectedOrderType={selectedOrderType}
                          hasUnsavedOrderType={hasUnsavedOrderType}
                          items={items}
                          setActiveOrder={setActiveOrder}
                        />
                        {menuItems
                          ? menuItems.map((item) => (
                              <Route
                                key={item.id}
                                path={`/${item.text.toLowerCase()}`}
                                children={({ match }) => {
                                  return (
                                    <ClassNames>
                                      {({ cx }) => (
                                        <li
                                          role="presentation"
                                          className={cx({
                                            'is-active': !!match,
                                          })}
                                          onClick={() => isUserLoggedIn()}
                                        >
                                          <Link
                                            to={`/${item.text.toLowerCase()}`}
                                            css={[
                                              {
                                                '&:focus': {
                                                  color: 'red',
                                                },
                                              },
                                              match && {
                                                color: 'red',
                                              },
                                            ]}
                                          >
                                            <Glyphicon glyph={item.icon} />{' '}
                                            {item.text}
                                          </Link>
                                        </li>
                                      )}
                                    </ClassNames>
                                  )
                                }}
                              />
                            ))
                          : null}
                      </React.Fragment>
                      {logoutComponent()}
                    </Nav>
                  </Navbar.Collapse>
                </Navbar>
              </DesktopView>
              <MobileView>
                <ActiveOrderItems
                  moduleName={moduleName}
                  selectedOrderType={selectedOrderType}
                  hasUnsavedOrderType={hasUnsavedOrderType}
                  items={items}
                  setActiveOrder={setActiveOrder}
                  mobile
                />
                <AppBar
                  menuOpened={menuOpened}
                  setMenuOpened={setMenuOpened}
                  menuTitle={menuTitle}
                  menuItems={menuItems}
                  moduleName={moduleName}
                  logoutComponent={logoutComponent}
                  activeOrder={activeOrder}
                />
              </MobileView>
            </>
          )
        }}
      </Query>
    </React.Fragment>
  )
}

export default Header
